import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Grid,
  MenuItem,
  Button,
  FormHelperText,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import TypeTransfert from "constants/TypeTransfert";
import type { Perimetre, PerimetreModelForm, Territoire } from "models";
import { territoiresService, perimetresService, networkService } from "services";
import { useErrorHandler, useSnackbarErrorHandler } from "utils/errorHandling";
import LoadingScreen from "components/Template/LoadingScreen";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { routesConfig } from "config/app-config";
import { useNavigate } from "react-router";
import { useSearch } from "providers";
import ControlledTextField from "components/inputs/ControlledTextField";
import ControlledDateTime from "components/inputs/ControlledDateTime";
import ToastMessages from "constants/ToastMessages";
import CompetenceChip from "components/Typography/CompetenceChip";
import { competencesAssociationList } from "constants/CompetenceMap";
import { enqueueSnackbar } from "notistack";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { validDateRules } from "constants/InputRules";
import AddIcon from "@mui/icons-material/Add";

type CentreFinancierMode = "existing" | "automatic";

function PerimetreForm(): ReactNode {
  const [territoires, setTerritoires] = useState<Array<Territoire>>([]);
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const [perimetreWithSameCodeSdea, setPerimetreWithSameCodeSdea] = useState<Perimetre>();
  const navigate = useNavigate();
  const { update, addOptimisticPerimetre } = useSearch();

  const formMethods = useForm<PerimetreModelForm>({ shouldFocusError: false });
  const { handleSubmit, control } = formMethods;

  const codeSdea = useWatch({ control, name: "codeSdea" });
  const competence = useWatch({ control, name: "competence" });

  const [centreFinancierMode, setCentreFinancierMode] = useState<CentreFinancierMode>("automatic");

  const { catchErrors, isLoading } = useErrorHandler();
  // useSnackbarErrorHandler permet de préciser à l'utilisateur la faute dans la requête qui n'aurait pas permis de créer le périmètre
  const { catchErrors: catchCreateErrors, isLoading: isSending } = useSnackbarErrorHandler();

  useEffect(() => {
    async function getTerritoires(): Promise<void> {
      const _territoires = await territoiresService.getAll();
      setTerritoires(_territoires);
    }

    async function getPerimetres(): Promise<void> {
      const _perimetres = await perimetresService.getAll();
      // Tri par nom puis compétence pour faciliter la recherche du périmètre sans autocomplete
      const sortedPerimetres = _perimetres.toSorted((a, b) => {
        const libelleCompare = a.libelle.localeCompare(b.libelle);
        return libelleCompare === 0 ? a.competence.localeCompare(b.competence) : libelleCompare;
      });
      setPerimetres(sortedPerimetres);
    }

    void catchErrors(getTerritoires);
    void catchErrors(getPerimetres);
  }, [catchErrors]);

  useEffect(() => {
    setPerimetreWithSameCodeSdea(
      perimetres.find((p) => p.code === codeSdea && p.competence === competence)
    );
  }, [codeSdea, competence, perimetres]);

  const submitPerimetre: SubmitHandler<PerimetreModelForm> = async function (
    data: PerimetreModelForm
  ): Promise<void> {
    async function createPerimetre(): Promise<void> {
      /**
       * Dans le cas automatique, on n'envoie pas le centre financier dans la requête,
       * le back s'en chargera et on le récupérera à la réplication
       */
      if (centreFinancierMode === "automatic") {
        data.centreFinancier = undefined;
      }
      const createdPerimetre = await perimetresService.create(data);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.CREATED_PERIMETRE,
      });

      /**
       * On a reçu une 201 Created; le périmètre est créé dans les données maîtres,
       * on part du principe que tout va bien et on ajoute les données du périmètre
       * créé dans la recherche.
       */
      addOptimisticPerimetre(createdPerimetre);
      navigate(routesConfig.admin.path);

      /**
       * On attend que la réplication des données soit terminée (=que le périmètre créé
       * soit présent dans les tables de vues) pour mettre à jour toutes les listes de
       * la recherche.
       */
      await networkService.waitForReplication(
        async () => {
          await perimetresService.getById(createdPerimetre.id);
        },
        20,
        update,
        () => {
          enqueueSnackbar({
            variant: "warning",
            message: ToastMessages.LONG_REPLICATION,
          });
          update();
        }
      );
    }

    await catchCreateErrors(createPerimetre);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <Grid component="form" onSubmit={handleSubmit(submitPerimetre)} container spacing={4}>
      <FormProvider {...formMethods}>
        <Grid container spacing={4} size={12}>
          <Grid size={12}>
            <ControlledTextField
              name="libelle"
              defaultValue=""
              rules={{ required: "Saisir un nom est obligatoire" }}
              fullWidth
              label="Désignation du périmètre *"
              variant="outlined"
              placeholder="Commission locale de ..."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledTextField
              select
              name="competence"
              rules={{ required: "Choisir une compétence est obligatoire" }}
              label="Compétence du périmètre *">
              {competencesAssociationList.map((c) => (
                <MenuItem key={c.competence} value={c.competence}>
                  <CompetenceChip competence={c.competence} sx={{ marginRight: 1 }} />
                  {c.label}
                </MenuItem>
              ))}
            </ControlledTextField>
          </Grid>
          <Grid container size={{ xs: 12, md: 6 }}>
            <ControlledTextField
              name="codeSdea"
              rules={{ required: "Saisir un code Sdea est obligatoire" }}
              type="number"
              label="Code du périmètre *"
              variant="outlined"
              placeholder="A saisir"
            />
            {perimetreWithSameCodeSdea != null && (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <WarningIcon color="warning" />
                <FormHelperText>
                  Attention ! Ce code SDEA est déjà utilisé pour cette compétence par le périmètre{" "}
                  {perimetreWithSameCodeSdea.libelle}
                </FormHelperText>
              </Stack>
            )}
          </Grid>
          <Grid container size={{ xs: 12, sm: 6 }}>
            <ControlledDateTime
              name="dateMembre"
              rules={{
                required: "Choisir une date de création est obligatoire",
                ...validDateRules,
              }}
              label="Date de création *"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ControlledTextField
              select
              name="typeTransfert"
              rules={{ required: "Choisir un type de transfert est obligatoire" }}
              label="Type de transfert *">
              {Object.values(TypeTransfert).map((v) => (
                <MenuItem key={v} value={v}>
                  {v === TypeTransfert.PI ? "Transfert complet" : "Transfert partiel"}
                </MenuItem>
              ))}
            </ControlledTextField>
          </Grid>
          <Grid size={12}>
            <ControlledTextField
              select
              name="idTerritoire"
              rules={{ required: "Choisir un territoire est obligatoire" }}
              label="Territoire de rattachement *">
              {territoires.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.libelle}
                </MenuItem>
              ))}
            </ControlledTextField>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">Centre financier</Typography>
          </Grid>
          <Grid size={12}>
            <RadioGroup
              row
              value={centreFinancierMode}
              onChange={(_, v) => {
                const mode = (v as CentreFinancierMode) ?? "automatic";
                setCentreFinancierMode(mode);
              }}>
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label="A partir d'un périmètre existant"
              />
              <FormControlLabel
                value="automatic"
                control={<Radio />}
                label="Génération automatique"
              />
            </RadioGroup>
          </Grid>
          <Grid size={12}>
            {centreFinancierMode === "existing" ? (
              <ControlledTextField
                select
                name="centreFinancier"
                rules={{ required: "Choisir un périmètre existant est obligatoire" }}
                label="Périmètre existant">
                {perimetres
                  .filter((c) => c.competence !== competence && c.centreFinancier != null)
                  .map((c) => (
                    <MenuItem key={c.code} value={c.centreFinancier}>
                      {c.libelle} - {c.competence} - Centre financier : {c.centreFinancier}
                    </MenuItem>
                  ))}
              </ControlledTextField>
            ) : (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <InfoIcon color="info" />
                <FormHelperText>
                  Le code du centre financier sera créé automatiquement et disponible à la
                  consultation.
                </FormHelperText>
              </Stack>
            )}
          </Grid>
        </Grid>
        <Grid container size={12} sx={{ justifyContent: "flex-end" }} spacing={1}>
          <Button href={routesConfig.admin.path} color="error">
            Annuler
          </Button>
          <Button variant="contained" type="submit" loading={isSending} startIcon={<AddIcon />}>
            Créer un nouveau périmètre
          </Button>
        </Grid>
      </FormProvider>
    </Grid>
  );
}

export default PerimetreForm;
